/** favorites/{uid} and recentlyAdded/{uid} — map docs keyed by food_id (port of FavoritesActions). */
import { doc, onSnapshot, setDoc, updateDoc, deleteField } from 'firebase/firestore'
import { db } from '@/lib/firebase'
import { track } from '@/lib/analytics'
import { stripUndefined } from '@/lib/format'
import type { QuickFoodItem } from '@/types/entries'

type MapDoc = Record<string, QuickFoodItem>

function subscribeMap(col: 'favorites' | 'recentlyAdded', uid: string, onChange: (items: MapDoc) => void): () => void {
  return onSnapshot(
    doc(db, col, uid),
    (snap) => onChange((snap.data() as MapDoc) || {}),
    (err) => {
      console.error(`subscribe ${col}`, err)
      onChange({})
    },
  )
}

export const subscribeFavorites = (uid: string, onChange: (items: MapDoc) => void) => subscribeMap('favorites', uid, onChange)
export const subscribeRecents = (uid: string, onChange: (items: MapDoc) => void) => subscribeMap('recentlyAdded', uid, onChange)

export async function addFavorite(uid: string, item: Partial<QuickFoodItem> & { food_id: string; food_name: string }): Promise<void> {
  const fav: QuickFoodItem = {
    food_id: item.food_id,
    food_name: item.food_name || '',
    brand_name: item.brand_name || '',
    type: item.type || '',
    calories: item.calories || 0,
    fats: item.fats || 0,
    proteins: item.proteins || 0,
    totalcarbs: item.totalcarbs || 0,
    fiber: item.fiber || 0,
    serving_size: item.serving_size || '',
    timestamp: new Date().toISOString(),
    ...(item.nutrients ? { nutrients: item.nutrients } : {}),
    ...(item.image_url ? { image_url: item.image_url } : {}),
    ...(item.isAIScan ? { isAIScan: true, ingredients: item.ingredients || [], source: item.source || 'camera' } : {}),
  }
  await setDoc(doc(db, 'favorites', uid), { [item.food_id]: stripUndefined(fav) }, { merge: true })
  track('favorite_added', { food_source: fav.type })
}

export async function removeFavorite(uid: string, foodId: string): Promise<void> {
  await updateDoc(doc(db, 'favorites', uid), { [foodId]: deleteField() })
  track('favorite_removed', {})
}

export async function removeRecent(uid: string, foodId: string): Promise<void> {
  await updateDoc(doc(db, 'recentlyAdded', uid), { [foodId]: deleteField() })
}

export async function clearRecents(uid: string): Promise<void> {
  await setDoc(doc(db, 'recentlyAdded', uid), {})
}

export async function clearFavorites(uid: string): Promise<void> {
  await setDoc(doc(db, 'favorites', uid), {})
}
